import { useSearchParams, useNavigate } from 'react-router-dom';

// State keys that should be persisted in URL
export const URL_STATE_KEYS = {
  ZONE: 'zone',
  STYLE: 'style',
  SUNLIGHT: 'sunlight',
  WINDOWS: 'windows',
  FACADE: 'facade',
  HOUSE_SIZE: 'houseSize',
  SHAPE: 'shape',
  BED_SIZE: 'bedSize',
  BED_CONFIG: 'bedConfig',
  DESIGN_NAME: 'designName',
  DESIGN_ID: 'designId',
  USER_ID: 'userId'
};

// Helper function to get URL search params as an object
export const getUrlParams = (searchParams) => {
  const params = {};
  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }
  return params;
};

// Helper function to update URL with new params while preserving existing ones
export const updateUrlParams = (searchParams, newParams) => {
  const updatedParams = new URLSearchParams(searchParams);
  
  Object.entries(newParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      updatedParams.set(key, value.toString());
    } else {
      updatedParams.delete(key);
    }
  });
  
  return updatedParams;
};

// Custom hook to manage URL state
export const useUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get a specific parameter from URL
  const getUrlParam = (key) => {
    return searchParams.get(key);
  };

  // Set a specific parameter in URL
  const setUrlParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value !== null && value !== undefined && value !== '') {
      newParams.set(key, value.toString());
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Set multiple parameters at once
  const setUrlParams = (params) => {
    const newParams = updateUrlParams(searchParams, params);
    setSearchParams(newParams);
  };

  // Get all URL parameters as an object
  const getAllUrlParams = () => {
    return getUrlParams(searchParams);
  };

  // Clear all URL parameters
  const clearUrlParams = () => {
    setSearchParams(new URLSearchParams());
  };

  // Navigate to a new route while preserving URL parameters
  const navigateWithParams = (path, additionalParams = {}) => {
    const currentParams = getUrlParams(searchParams);
    const newParams = { ...currentParams, ...additionalParams };
    const queryString = new URLSearchParams(newParams).toString();
    const fullPath = queryString ? `${path}?${queryString}` : path;
    navigate(fullPath);
  };

  return {
    getUrlParam,
    setUrlParam,
    setUrlParams,
    getAllUrlParams,
    clearUrlParams,
    navigateWithParams,
    searchParams
  };
};

// Helper function to sync context state with URL params
export const syncStateWithUrl = (state, setUrlParams) => {
  const urlParams = {};
  
  if (state.currentDesign) {
    const design = state.currentDesign;
    
    if (design.zone) urlParams[URL_STATE_KEYS.ZONE] = design.zone;
    if (design.style) urlParams[URL_STATE_KEYS.STYLE] = design.style;
    if (design.sunlight) urlParams[URL_STATE_KEYS.SUNLIGHT] = design.sunlight;
    if (design.windows) urlParams[URL_STATE_KEYS.WINDOWS] = design.windows;
    if (design.facade) urlParams[URL_STATE_KEYS.FACADE] = design.facade;
    if (design.houseSize) urlParams[URL_STATE_KEYS.HOUSE_SIZE] = design.houseSize;
    if (design.shape) urlParams[URL_STATE_KEYS.SHAPE] = design.shape;
    if (design.bedSize) urlParams[URL_STATE_KEYS.BED_SIZE] = design.bedSize;
    if (design.bedConfig) urlParams[URL_STATE_KEYS.BED_CONFIG] = design.bedConfig;
    if (design.designName) urlParams[URL_STATE_KEYS.DESIGN_NAME] = design.designName;
    if (design.id) urlParams[URL_STATE_KEYS.DESIGN_ID] = design.id;
  }
  
  if (state.userId) urlParams[URL_STATE_KEYS.USER_ID] = state.userId;
  
  setUrlParams(urlParams);
};

// Helper function to restore state from URL params
export const restoreStateFromUrl = (urlParams, dispatch, actionTypes) => {
  const restoredState = {};
  console.log('urlParams:', urlParams);
  if (urlParams[URL_STATE_KEYS.ZONE]) {
    dispatch({ type: actionTypes.SET_ZONE, payload: urlParams[URL_STATE_KEYS.ZONE] });
    restoredState.zone = urlParams[URL_STATE_KEYS.ZONE];
  }
  
  if (urlParams[URL_STATE_KEYS.STYLE]) {
    dispatch({ type: actionTypes.SET_STYLE, payload: urlParams[URL_STATE_KEYS.STYLE] });
    restoredState.style = urlParams[URL_STATE_KEYS.STYLE];
  }
  
  if (urlParams[URL_STATE_KEYS.SUNLIGHT]) {
    dispatch({ type: actionTypes.SET_SUNLIGHT, payload: urlParams[URL_STATE_KEYS.SUNLIGHT] });
    restoredState.sunlight = urlParams[URL_STATE_KEYS.SUNLIGHT];
  }
  
  if (urlParams[URL_STATE_KEYS.WINDOWS]) {
    dispatch({ type: actionTypes.SET_WINDOWS, payload: urlParams[URL_STATE_KEYS.WINDOWS] });
    restoredState.windows = urlParams[URL_STATE_KEYS.WINDOWS];
  }
  
  if (urlParams[URL_STATE_KEYS.FACADE]) {
    console.log('urlParams[URL_STATE_KEYS.FACADE]:', urlParams[URL_STATE_KEYS.FACADE]);
    dispatch({ type: actionTypes.SET_FACADE, payload: urlParams[URL_STATE_KEYS.FACADE] });
    restoredState.facade = urlParams[URL_STATE_KEYS.FACADE];
  }
  
  if (urlParams[URL_STATE_KEYS.HOUSE_SIZE]) {
    const houseSize = parseInt(urlParams[URL_STATE_KEYS.HOUSE_SIZE]);
    dispatch({ type: actionTypes.SET_HOUSE_SIZE, payload: houseSize });
    restoredState.houseSize = houseSize;
  }
  
  if (urlParams[URL_STATE_KEYS.SHAPE]) {
    dispatch({ type: actionTypes.SET_SHAPE, payload: urlParams[URL_STATE_KEYS.SHAPE] });
    restoredState.shape = urlParams[URL_STATE_KEYS.SHAPE];
  }
  
  if (urlParams[URL_STATE_KEYS.BED_SIZE]) {
    const bedSize = parseInt(urlParams[URL_STATE_KEYS.BED_SIZE]);
    dispatch({ type: actionTypes.SET_BED_SIZE, payload: bedSize });
    restoredState.bedSize = bedSize;
  }
  
  if (urlParams[URL_STATE_KEYS.BED_CONFIG]) {
    const bedConfig = parseInt(urlParams[URL_STATE_KEYS.BED_CONFIG]);
    dispatch({ type: actionTypes.SET_BED_CONFIG, payload: bedConfig });
    restoredState.bedConfig = bedConfig;
  }
  
  if (urlParams[URL_STATE_KEYS.DESIGN_NAME]) {
    dispatch({ type: actionTypes.SET_DESIGN_NAME, payload: urlParams[URL_STATE_KEYS.DESIGN_NAME] });
    restoredState.designName = urlParams[URL_STATE_KEYS.DESIGN_NAME];
  }
  
  return restoredState;
};
